<script setup lang="ts">
import { getMetaTags } from '~/utils/meta'
import { svgIcons } from '~/models/images'
import { videos } from '~/models/files'
import { heroImages } from '#shared/models/images'

interface WhoDoWeHelpI18n {
  title: string
  description: string
  'image-DNT': keyof typeof svgIcons
  'aria-label': string
}

interface WhoDoWeHelpSection {
  title: string
  description: string
  component: Component
  ariaLabel: string
}

interface HowItWorkSection {
  title: string
  paragraph: string
}

definePageMeta({
  layout: false,
  scrollToTop: false
})

const heroImage = heroImages['ilse-en-frans']

const nikkiVideoMp4 = videos.nikki

const { t, tm, baseKey } = useI18n('pages.index')

useSeoMeta(
  getMetaTags(
    {
      title: '.meta.title',
      description: '.meta.description'
    },
    heroImage.src,
    baseKey
  )
)

useSchemaOrg(
  getSchemaOrg({
    title: 'links.thuisgekookt'
  })
)

const whoDoWeHelpSection = computed<WhoDoWeHelpSection[]>(() => {
  const items: WhoDoWeHelpI18n[] = tm('.section-two.items')

  return items.map((item) => {
    const iconComponent = svgIcons[item['image-DNT']]
    return {
      title: item.title,
      description: item.description,
      component: iconComponent,
      ariaLabel: item['aria-label']
    }
  })
})

const howItWorksSection = computed<HowItWorkSection[]>(
  () => tm('.section-three.paragraphs') as HowItWorkSection[]
)
</script>

<template>
  <div>
    <NuxtLayout
      name="default"
      title=".hero.title"
      description=".hero.description"
      :image="heroImage"
      :ui="{
        image: 'lg:object-[center_-3rem]'
      }"
    >
      <!-- TODO: ^^^^ Replace with actual dark mode hero later -->
      <template #buttons>
        <VButton color="black" dark-mode-color="beige" :to="{ name: 'maaltijden' }">
          {{ t('buttons.foodie') }}
        </VButton>
        <VButton color="beige" dark-mode-color="success" :to="{ name: 'thuiskok' }">
          {{ t('buttons.cook') }}
        </VButton>
      </template>

      <VSection base-key=".section-one" description=".description" />

      <VSection base-key=".section-two" title=".title">
        <template #content>
          <VCarousel
            v-slot="{ item }"
            :items="whoDoWeHelpSection"
            :number-of-items="{ mobile: 1, tablet: 2, desktop: 3, widescreen: 4 }"
            loop
            dots
            sr-label=".carousel-aria-label"
          >
            <VCard
              full-height
              class="rounded-xl"
              :ui="{
                container: 'bg-beige-900 dark:bg-dark-green-700 px-5 pb-0',
                headerTitle: 'font-bold',
                footer: 'pt-0'
              }"
            >
              <template #image>
                <component
                  :is="item.component"
                  height="240px"
                  width="100%"
                  class="min-h-60 bg-beige-600 fill-dark-green dark:bg-dark-green dark:fill-gray-100"
                />
              </template>
              <template #content>
                <div>
                  <h3 class="text-h3 font-bold text-dark-green dark:text-success">
                    {{ item.title }}
                  </h3>
                  <p class="mt-2 dark:text-gray-100">
                    {{ item.description }}
                  </p>
                </div>
              </template>
              <template #footer>
                <div class="relative my-0 flex justify-end">
                  <VButton
                    icon-only="fa-arrow-right-long"
                    color="transparent"
                    class="height-12.5 max-w-12.5 hover:border-0! dark:*:text-gray-100"
                    size="medium"
                    :aria-label="item.ariaLabel"
                    :to="{ name: 'maaltijden' }"
                  />
                </div>
              </template>
            </VCard>
          </VCarousel>

          <div class="section-button">
            <VButton :to="{ name: 'maaltijden' }" label="buttons.sign-up" />
          </div>
        </template>
      </VSection>

      <VSection base-key=".section-three" title=".title">
        <template
          v-for="(paragraph, index) in howItWorksSection"
          :key="`section-three-${index}`"
        >
          <h3 class="paragraph-title">{{ paragraph.title }}</h3>
          <p>
            {{ paragraph.paragraph }}
          </p>
        </template>
      </VSection>

      <VSection>
        <template #content>
          <div class="flex justify-center">
            <VVideo
              :videos="{ src: nikkiVideoMp4, type: 'video/mp4' }"
              class="rounded-2xl"
              width="320"
              height="570"
            />
          </div>

          <div class="section-button">
            <VButton :to="{ name: 'maaltijden' }" label="buttons.sign-up" />
          </div>
        </template>
      </VSection>

      <LazyExperiencesOfOtherFoodiesSection />
      <LazyHowCanYouHelpSection />

      <LazyLocationsSection
        id="focus-municipalities"
        title="titles.our-focus-municipalities"
        description="descriptions.locations-section"
        page-exists
      />

      <LazyFAQSection hide-button />
    </NuxtLayout>
  </div>
</template>

<style scoped></style>
